import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import ChipBoard from './ChipBoard';
import GameTable from './GameTable';

function GameRoom({ user, room, onLogout }) {
  const [players, setPlayers] = useState([]);
  const [gameSession, setGameSession] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPlayers = async () => {
    if (!room?.id) return;
    try {
      const { data, error } = await supabase
        .from('players')
        .select('*')
        .eq('room_id', room.id)
        .order('seat_position');

      if (error) throw error;
      setPlayers(data || []);
    } catch (error) {
      console.error('获取玩家列表失败:', error);
    }
  };

  const fetchGameSession = async () => {
    if (!room?.id) return;
    try {
      const { data, error } = await supabase
        .from('game_sessions')
        .select('*')
        .eq('room_id', room.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;
      setGameSession(data?.[0] || null);
      setLoading(false);
    } catch (error) {
      console.error('获取游戏状态失败:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!room?.id) return;

    const setupRealtimeSubscriptions = () => {
      console.log('🔄 设置实时订阅 for room:', room.id);
      
      // 创建一个统一的频道来监听所有房间变化
      const roomChannel = supabase
        .channel(`room_${room.id}_realtime`, {
          config: {
            broadcast: { self: false },
            presence: { key: user.id }
          }
        })
        .on('postgres_changes', {
          event: '*',
          schema: 'public', 
          table: 'players',
          filter: `room_id=eq.${room.id}`
        }, (payload) => {
          console.log('👥 玩家变化事件:', payload.eventType, payload.new, payload.old);
          // 延迟一点再获取，确保数据库已更新
          setTimeout(() => {
            fetchPlayers();
          }, 100);
        })
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'game_sessions', 
          filter: `room_id=eq.${room.id}`
        }, (payload) => {
          console.log('🎮 游戏状态变化:', payload.eventType, payload.new);
          setTimeout(() => {
            fetchGameSession();
          }, 100);
        })
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'game_actions'
        }, (payload) => {
          console.log('⚡ 游戏行动变化:', payload.eventType, payload.new);
          setTimeout(() => {
            fetchGameSession();
          }, 100);
        })
        .subscribe((status, err) => {
          if (status === 'SUBSCRIBED') {
            console.log('✅ 实时订阅成功建立');
          } else if (status === 'CHANNEL_ERROR') {
            console.error('❌ 实时订阅出错:', err);
          } else if (status === 'TIMED_OUT') {
            console.error('⏰ 实时订阅超时');
          } else {
            console.log('📡 订阅状态变化:', status);
          }
        });

      // 添加定期刷新作为备选方案
      const refreshInterval = setInterval(() => {
        console.log('🔄 定期刷新数据 (备选方案)');
        fetchPlayers();
        fetchGameSession();
      }, 10000); // 每10秒刷新一次

      return () => {
        console.log('🧹 清理实时订阅和定时器');
        roomChannel.unsubscribe();
        clearInterval(refreshInterval);
      };
    };

    // 初始化数据加载
    fetchPlayers();
    fetchGameSession();
    const unsubscribe = setupRealtimeSubscriptions();

    return unsubscribe;
  }, [room?.id]);

  const startGame = async () => {
    if (!user.isHost) {
      alert('只有房主可以开始游戏');
      return;
    }

    if (players.length < 2) {
      alert('至少需要2名玩家才能开始游戏');
      return;
    }

    try {
      // 创建新的游戏会话
      const { error } = await supabase
        .from('game_sessions')
        .insert([{
          room_id: room.id,
          session_number: 1,
          pot_amount: 0,
          current_bet: 0,
          current_player_id: players[0]?.id,
          game_stage: 'preflop',
          status: 'active'
        }])
        .select()
        .single();

      if (error) throw error;

      // 更新房间状态
      await supabase
        .from('rooms')
        .update({ status: 'playing' })
        .eq('id', room.id);

      alert('游戏开始！');
    } catch (error) {
      console.error('开始游戏失败:', error);
      alert('开始游戏失败，请重试');
    }
  };

  const endGame = async () => {
    if (!user.isHost) {
      alert('只有房主可以结束游戏');
      return;
    }

    try {
      // 结束当前游戏会话
      if (gameSession) {
        await supabase
          .from('game_sessions')
          .update({ status: 'finished' })
          .eq('id', gameSession.id);
      }

      // 更新房间状态
      await supabase
        .from('rooms')
        .update({ status: 'waiting' })
        .eq('id', room.id);

      alert('游戏结束！');
    } catch (error) {
      console.error('结束游戏失败:', error);
      alert('结束游戏失败，请重试');
    }
  };

  const updatePlayerChips = async (playerId, newChips) => {
    try {
      await supabase
        .from('players')
        .update({ chips: newChips })
        .eq('id', playerId);
    } catch (error) {
      console.error('更新筹码失败:', error);
    }
  };

  const handleGameAction = async (action, amount) => {
    if (!gameSession || gameSession.current_player_id !== user.id) {
      alert('不是您的回合');
      return;
    }

    try {
      const currentPlayer = players.find(p => p.id === user.id);
      if (!currentPlayer) {
        alert('找不到玩家信息');
        return;
      }

      let newChips = currentPlayer.chips;
      let actualAmount = 0;

      switch (action) {
        case 'fold':
          // 盖牌：玩家退出当前局
          await supabase
            .from('players')
            .update({ is_active: false })
            .eq('id', user.id);
          break;

        case 'call':
          // 跟注：跟上当前注额
          actualAmount = Math.min(amount, currentPlayer.chips);
          newChips = currentPlayer.chips - actualAmount;
          break;

        case 'raise':
          // 加注：下注指定金额
          if (amount > currentPlayer.chips) {
            alert('筹码不足');
            return;
          }
          actualAmount = amount;
          newChips = currentPlayer.chips - actualAmount;
          break;

        default:
          return;
      }

      // 记录游戏行动
      await supabase
        .from('game_actions')
        .insert([{
          game_session_id: gameSession.id,
          player_id: user.id,
          action_type: action,
          bet_amount: actualAmount,
          chips_after: newChips
        }]);

      // 更新玩家筹码
      if (action !== 'fold') {
        await supabase
          .from('players')
          .update({ chips: newChips })
          .eq('id', user.id);
      }

      // 更新游戏状态
      const newPotAmount = gameSession.pot_amount + actualAmount;
      const newCurrentBet = action === 'raise' ? actualAmount : gameSession.current_bet;
      
      // 找到下一个玩家
      const activePlayers = players.filter(p => p.is_active);
      const currentIndex = activePlayers.findIndex(p => p.id === user.id);
      const nextIndex = (currentIndex + 1) % activePlayers.length;
      const nextPlayer = activePlayers[nextIndex];

      await supabase
        .from('game_sessions')
        .update({
          pot_amount: newPotAmount,
          current_bet: newCurrentBet,
          current_player_id: nextPlayer?.id
        })
        .eq('id', gameSession.id);

      console.log(`执行了${action}操作，金额: ${actualAmount}`);
    } catch (error) {
      console.error('游戏行动失败:', error);
      alert('操作失败，请重试');
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p style={{ marginTop: '20px' }}>加载中...</p>
      </div>
    );
  }

  return (
    <div className="container">
      {/* 头部信息 */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div>
            <h2 style={{ margin: 0 }}>{room.name}</h2>
            <p style={{ margin: 0, color: '#9ca3af' }}>房间密钥: {room.key}</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              onClick={() => {
                console.log('🔄 手动刷新房间数据');
                fetchPlayers();
                fetchGameSession();
              }}
              className="btn btn-secondary"
              style={{ padding: '8px 16px', fontSize: '14px' }}
              title="手动刷新房间状态"
            >
              🔄
            </button>
            <button 
              onClick={onLogout}
              className="btn btn-secondary"
              style={{ padding: '8px 16px', fontSize: '14px' }}
            >
              退出房间
            </button>
          </div>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span className="status-indicator online"></span>
            在线玩家: {players.length}/6
          </div>
          <div>
            {user.isHost && (
              <div style={{ display: 'flex', gap: '10px' }}>
                {!gameSession ? (
                  <button 
                    onClick={startGame}
                    className="btn btn-success"
                    style={{ padding: '8px 16px', fontSize: '14px' }}
                  >
                    开始游戏
                  </button>
                ) : (
                  <button 
                    onClick={endGame}
                    className="btn btn-danger"
                    style={{ padding: '8px 16px', fontSize: '14px' }}
                  >
                    结束游戏
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 游戏桌面 */}
      <GameTable 
        players={players} 
        gameSession={gameSession}
        currentUser={user}
        onPlayerAction={updatePlayerChips}
        onGameAction={handleGameAction}
      />

      {/* 筹码板 */}
      <ChipBoard 
        players={players} 
        currentUser={user}
        onUpdateChips={updatePlayerChips}
      />

      {/* 显示当前玩家筹码 */}
      <div className="chip-display">
        💰 我的筹码: {players.find(p => p.id === user.id)?.chips || 0}
      </div>
    </div>
  );
}

export default GameRoom; 